import { Link } from "react-router-dom";
import { Github, Twitter, Linkedin, Instagram, ArrowUpRight } from "lucide-react";
import Logo from "./Logo";
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { popConfetti } from "@/lib/confetti";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("You're in. Welcome to SECQRE.");
      popConfetti({ x: 0.5, y: 0.85 });
      setEmail("");
    } catch (err) {
      toast.error("Couldn't subscribe. Please check your email and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="border-t border-[#E2E8F0] bg-[#F8F9FA]">
      <div className="container-secqre py-20 md:py-24">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-8">
          <div className="lg:col-span-5">
            <Logo size="md" />
            <p className="mt-6 text-[#475569] max-w-md leading-relaxed">
              SECQRE turns your windshield into a private contact line. Masked calls,
              SMS &amp; WhatsApp — your number stays yours.
            </p>
            <form onSubmit={submit} className="mt-8 flex gap-2 max-w-md" data-testid="footer-newsletter-form">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@drivers.com"
                required
                className="flex-1 px-4 py-3 rounded-lg border border-[#E2E8F0] bg-white focus:outline-none focus:ring-2 focus:ring-[#0F172A]/30"
                data-testid="footer-newsletter-email"
              />
              <button type="submit" disabled={loading} className="btn-primary !py-3 !px-5" data-testid="footer-newsletter-submit">
                {loading ? "..." : "Subscribe"}
              </button>
            </form>
            <p className="mt-3 text-xs text-[#94a3b8]">Quarterly product letter. No spam — same promise as our masked calls.</p>
          </div>

          <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-4 gap-8">
            <FootCol title="Product" links={[
              { to: "/features", label: "Features" },
              { to: "/solutions", label: "Solutions" },
              { to: "/pricing", label: "Pricing" },
              { to: "/blog", label: "Blog" },
            ]} />
            <FootCol title="Company" links={[
              { to: "/about", label: "About" },
              { to: "/contact", label: "Contact" },
              { to: "/blog", label: "Stories" },
            ]} />
            <FootCol title="Resources" links={[
              { to: "/features", label: "How it works" },
              { to: "/solutions", label: "Fleet" },
              { to: "/solutions", label: "Resellers" },
            ]} />
            <FootCol title="Legal" links={[
              { to: "/about", label: "Privacy" },
              { to: "/about", label: "Terms" },
              { to: "/contact", label: "Support" },
            ]} />
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-[#E2E8F0] flex flex-col-reverse md:flex-row items-start md:items-center justify-between gap-6">
          <p className="text-sm text-[#475569]">© {new Date().getFullYear()} SECQRE Inc. · Crafted for private drivers. United States.</p>
          <div className="flex items-center gap-3">
            <SocialIcon Icon={Twitter} label="Twitter" />
            <SocialIcon Icon={Linkedin} label="LinkedIn" />
            <SocialIcon Icon={Instagram} label="Instagram" />
            <SocialIcon Icon={Github} label="Github" />
          </div>
        </div>
      </div>
    </footer>
  );
}

const FootCol = ({ title, links }) => (
  <div>
    <h4 className="text-sm font-bold text-[#0A0A0A] mb-4">{title}</h4>
    <ul className="space-y-3">
      {links.map((l, i) => (
        <li key={i}>
          <Link
            to={l.to}
            className="group inline-flex items-center gap-1 text-sm text-[#475569] hover:text-[#0A0A0A] transition-colors"
            data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            {l.label}
            <ArrowUpRight className="w-3.5 h-3.5 opacity-0 -translate-y-0.5 group-hover:opacity-60 group-hover:translate-y-0 transition-all" />
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

const SocialIcon = ({ Icon, label }) => (
  <a
    href="#"
    aria-label={label}
    data-testid={`social-${label.toLowerCase()}`}
    className="w-9 h-9 inline-flex items-center justify-center rounded-md border border-[#E2E8F0] bg-white text-[#475569] hover:text-[#0A0A0A] hover:border-[#0A0A0A] transition-colors"
    onClick={(e) => e.preventDefault()}
  >
    <Icon className="w-4 h-4" />
  </a>
);
